import type { User } from "firebase/auth";
import { profileToGameArt } from "@/character/spritesheet";
import { loadCharacterProfile } from "@/character/storage";
import {
  getDungeonFloorConfig,
  hydrateDungeonFloorConfigs,
  listDungeonFloorConfigs,
  saveDungeonFloorConfig,
} from "@/dungeon/dungeon-floor-config";
import {
  DUNGEON_FLOOR_MAX_MONSTER_TYPES,
  mountDungeonFloorMonsterModal,
  type DungeonSpawnDraftRow,
} from "@/dungeon/dungeon-floor-monster-modal";
import { mountDungeonFloorMonsterPickerModal } from "@/dungeon/dungeon-floor-monster-picker-modal";
import {
  commitFloorSpawnsToVault,
  maxMonsterTotalOnFloor,
  migrateEconomyMonsterVaultFromLegacyFloors,
  vaultStock,
} from "@/dungeon/dungeon-monster-vault";
import {
  dungeonFloorCanEnable,
  dungeonFloorEnableBlockedMessage,
  dungeonFloorMissingRequirements,
} from "@/dungeon/dungeon-floor-readiness";
import { dungeonSceneId, loadDungeonMeta } from "@/dungeon/dungeon-meta";
import {
  applyDungeonMonsterPowerCard,
  dungeonMonsterPowerCardCount,
  stepDungeonMonsterPowerDown,
} from "@/dungeon/dungeon-monster-power-card";
import { hydrateDungeonShopConfig } from "@/dungeon/load-dungeon-shop-config";
import {
  GM_MONSTER_PLACEHOLDER_IMAGE,
  hydrateGmMonsterCatalog,
  loadGmMonsterCatalog,
  type GmMonsterTemplate,
} from "@/gm/gm-monster-catalog";
import { HomeEvents } from "@/home/home-events";
import { HOME_ASSETS } from "@/home/home-assets";
import { createDefaultHomeEconomy, type HomeEconomy } from "@/home/economy";
import { loadHomeEconomyFromIdb, saveHomeEconomyToIdb } from "@/home/home-idb";
import {
  DUNGEON_FLOOR_EDITOR_FEATURES,
  mountHomeSceneEditor,
  type HomeSceneEditorHandle,
} from "@/home/mount-home-scene-editor";
import { loadHomeRoom, type HomeRoomData } from "@/home/storage";
import { loadSceneRoom, saveSceneRoom } from "@/home/scene-room-storage";
import { openDungeonTrialModal } from "@/mount-dungeon-trial-modal";
import { buildTowerArenaAmbienceConfig } from "@/tower/tower-arena-ambience";
import {
  homeRoomToTowerScenePlacements,
  mergeHomeCatalogParts,
  resolveDungeonFloorScenePlacements,
  towerFloorToHomeRoom,
} from "@/tower/tower-scene-room-adapter";
import { TOWER_ARENA_COLS, TOWER_ARENA_ROWS } from "@/tower/tower-floor-config";
import {
  clampSpawnPerMinute,
  TOWER_DEFAULT_SPAWN_PER_MINUTE,
} from "@/tower/tower-spawn-rate";

function escapeHtml(text: string): string {
  return text
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function monsterThumb(m: GmMonsterTemplate | undefined): string {
  if (!m) return GM_MONSTER_PLACEHOLDER_IMAGE;
  return m.imageDataUrl?.startsWith("data:") ? m.imageDataUrl : GM_MONSTER_PLACEHOLDER_IMAGE;
}

function applyDungeonFloorLayoutFromConfig(
  room: HomeRoomData,
  configRoom: HomeRoomData,
): HomeRoomData {
  if (room.placements.length > 0) return room;
  if (configRoom.placements.length === 0) return room;
  return {
    ...room,
    placements: configRoom.placements,
    themeId: configRoom.themeId,
    backdropPartId: configRoom.backdropPartId,
    backdropUrl: configRoom.backdropUrl,
    shellPartId: configRoom.shellPartId,
    floorPartId: configRoom.floorPartId,
  };
}

export type MountDungeonFloorPageOptions = {
  onRequestClose?: () => void;
};

export async function mountDungeonFloorPage(
  root: HTMLElement,
  user: User,
  floor: number,
  options?: MountDungeonFloorPageOptions,
): Promise<() => void> {
  const profile = loadCharacterProfile(user.uid);
  if (!profile) {
    window.location.replace("/character/create");
    return () => {};
  }

  await hydrateGmMonsterCatalog();
  const shop = await hydrateDungeonShopConfig();
  const catalog = loadGmMonsterCatalog();

  const meta = loadDungeonMeta(user.uid);
  const editingFloor = Math.min(meta.maxFloors, Math.max(1, Math.floor(floor)));
  await hydrateDungeonFloorConfigs(user.uid);
  const cfg = getDungeonFloorConfig(editingFloor);

  let economy: HomeEconomy =
    (await loadHomeEconomyFromIdb(user.uid)) ?? createDefaultHomeEconomy();

  const legacyFloorPower = cfg?.powerMultiplier ?? 1;

  let rowSeq = 0;
  const nextRowId = () => {
    rowSeq += 1;
    return `row-${rowSeq}`;
  };

  let draftRows: DungeonSpawnDraftRow[] = (cfg?.spawns ?? []).map((row) => ({
    rowId: nextRowId(),
    monsterId: row.monsterId,
    count: row.count,
    powerMultiplier: row.powerMultiplier ?? (legacyFloorPower > 1 ? legacyFloorPower : 1),
    spawnPerMinute: row.spawnPerMinute ?? TOWER_DEFAULT_SPAWN_PER_MINUTE,
  }));
  let draftEnabled = cfg?.enabled ?? true;
  let savedSpawnsOnFloor = cfg?.spawns ?? [];
  const migrated = migrateEconomyMonsterVaultFromLegacyFloors(
    economy,
    listDungeonFloorConfigs(),
  );
  economy = migrated.economy;
  if (migrated.changed) {
    void saveHomeEconomyToIdb(user.uid, economy);
  }
  let sceneRoom = towerFloorToHomeRoom(cfg);
  let editor: HomeSceneEditorHandle | null = null;
  let status = "";

  const persistEconomy = () => {
    void saveHomeEconomyToIdb(user.uid, economy);
  };

  const monsterLabel = (monsterId: string): string =>
    catalog.find((c) => c.id === monsterId)?.name ?? monsterId;

  const currentScenePlacementCount = (): number => {
    const room = editor?.getRoomData() ?? sceneRoom;
    return room.placements.length;
  };

  const maxForMonster = (monsterId: string) =>
    maxMonsterTotalOnFloor(economy, savedSpawnsOnFloor, monsterId);

  const collectMissingForEnable = () =>
    dungeonFloorMissingRequirements({
      spawns: draftRows,
      economy,
      savedSpawnsOnFloor,
      scenePlacementCount: currentScenePlacementCount(),
      monsterName: monsterLabel,
    });

  const renderReadinessHtml = () => {
    const missing = collectMissingForEnable();
    if (missing.length === 0) {
      return `<p class="hint dungeon-floor-readiness dungeon-floor-readiness--ok">พร้อมเปิดด่าน — ติ๊ก «เปิดด่านนี้» แล้วกดบันทึก</p>`;
    }
    return `<div class="dungeon-floor-readiness dungeon-floor-readiness--warn" role="status">
      <p class="dungeon-floor-readiness-title">ยังเปิดด่านไม่ได้ — ขาด:</p>
      <ul class="dungeon-floor-readiness-list">
        ${missing.map((line) => `<li>${escapeHtml(line)}</li>`).join("")}
      </ul>
    </div>`;
  };

  const syncReadinessPanel = () => {
    const panel = root.querySelector<HTMLElement>("#dungeon-floor-readiness");
    if (panel) panel.innerHTML = renderReadinessHtml();
  };

  const syncEnableCheckbox = () => {
    const input = root.querySelector<HTMLInputElement>("#dungeon-enabled");
    if (input) input.checked = draftEnabled;
  };

  const ownedMonsterIds = (): string[] => {
    const ids = new Set<string>();
    for (const m of catalog) {
      if (vaultStock(economy, m.id) > 0 || maxForMonster(m.id) > 0) ids.add(m.id);
    }
    for (const row of draftRows) {
      if (row.monsterId) ids.add(row.monsterId);
    }
    return [...ids];
  };

  const usedMonsterIds = (excludeRowId?: string): string[] =>
    draftRows
      .filter((r) => r.rowId !== excludeRowId)
      .map((r) => r.monsterId);

  const selectableMonsterIds = (excludeRowId?: string): string[] => {
    const used = new Set(usedMonsterIds(excludeRowId));
    return ownedMonsterIds().filter((id) => !used.has(id));
  };

  const renderVaultHint = () => {
    const withStock = catalog.filter((m) => vaultStock(economy, m.id) > 0);
    if (withStock.length === 0) {
      return `<p class="hint">คลังว่าง — <a href="/home?dungeon=1&amp;shop=monsters">ซื้อมอนสเตอร์ที่ร้าน</a></p>`;
    }
    const lines = withStock
      .slice(0, 6)
      .map(
        (m) =>
          `${escapeHtml(m.name)} ${vaultStock(economy, m.id).toLocaleString("en-US")}`,
      )
      .join(" · ");
    const more = withStock.length > 6 ? ` · +${withStock.length - 6}` : "";
    return `<p class="hint dungeon-vault-summary">คลัง: ${lines}${more} · <a href="/home?dungeon=1&amp;shop=monsters">ซื้อเพิ่ม</a></p>`;
  };

  const renderSpawnList = () => {
    if (ownedMonsterIds().length === 0) {
      return `<p class="hint">ซื้อมอนสเตอร์ที่ร้านก่อน แล้วกดเพิ่ม</p>`;
    }
    if (draftRows.length === 0) {
      return `<p class="hint">กด «เพิ่มมอนสเตอร์» (สูงสุด ${DUNGEON_FLOOR_MAX_MONSTER_TYPES} ประเภท)</p>`;
    }
    return `<ul class="dungeon-spawn-list" role="list">
      ${draftRows
        .map((row) => {
          const m = catalog.find((c) => c.id === row.monsterId);
          const power = row.powerMultiplier ?? 1;
          const name = m?.name ?? row.monsterId;
          return `
        <li class="dungeon-spawn-list-item" role="listitem">
          <button type="button" class="dungeon-spawn-list-open" data-row-id="${escapeHtml(row.rowId)}" aria-label="รายละเอียด ${escapeHtml(name)}">
            <img class="dungeon-spawn-list-thumb" src="${escapeHtml(monsterThumb(m))}" alt="" width="40" height="40" />
          </button>
          <div class="dungeon-spawn-list-controls">
            <div class="dungeon-spawn-stepper" title="จำนวน">
              <button type="button" class="dungeon-spawn-stepper-btn" data-row-count-minus data-row-id="${escapeHtml(row.rowId)}" aria-label="ลดจำนวน">−</button>
              <span class="dungeon-spawn-stepper-value">${row.count.toLocaleString("en-US")}</span>
              <button type="button" class="dungeon-spawn-stepper-btn" data-row-count-plus data-row-id="${escapeHtml(row.rowId)}" aria-label="เพิ่มจำนวน">+</button>
            </div>
            <div class="dungeon-spawn-stepper dungeon-spawn-stepper--power" title="คูณพลัง">
              <button type="button" class="dungeon-spawn-stepper-btn" data-row-power-minus data-row-id="${escapeHtml(row.rowId)}" aria-label="ลดคูณพลัง">−</button>
              <span class="dungeon-spawn-stepper-value">×${power}</span>
              <button type="button" class="dungeon-spawn-stepper-btn" data-row-power-plus data-row-id="${escapeHtml(row.rowId)}" aria-label="เพิ่มคูณพลัง">+</button>
            </div>
          </div>
          <button type="button" class="btn btn-ghost btn-icon dungeon-spawn-list-remove" data-row-id="${escapeHtml(row.rowId)}" aria-label="ลบ ${escapeHtml(name)}">×</button>
        </li>`;
        })
        .join("")}
    </ul>`;
  };

  const renderAside = () => `
    <section class="dungeon-aside-section dungeon-aside-section--rows">
      <div class="dungeon-aside-section-head">
        <h3>มอนสเตอร์ในด่าน</h3>
        <span class="hint dungeon-spawn-cap">${draftRows.length}/${DUNGEON_FLOOR_MAX_MONSTER_TYPES}</span>
      </div>
      <div id="dungeon-vault-hint">${renderVaultHint()}</div>
      <p class="hint dungeon-power-card-hint">บัตรคูณพลัง <strong>${dungeonMonsterPowerCardCount(economy)}</strong> ใบ · <a href="/home?shop=cash">ซื้อร้าน Cash</a> (10/ใบ +1×)</p>
      <div id="dungeon-rows" class="dungeon-spawn-rows">${renderSpawnList()}</div>
      <button type="button" class="btn btn-ghost btn-sm dungeon-add-row-btn" id="dungeon-add-row" ${draftRows.length >= DUNGEON_FLOOR_MAX_MONSTER_TYPES ? "disabled" : ""}>+ เพิ่มมอนสเตอร์</button>
      <div id="dungeon-floor-readiness" aria-live="polite">${renderReadinessHtml()}</div>
    </section>
  `;

  const mountDungeonChrome = () => {
    let chrome = root.querySelector<HTMLElement>("#dungeon-floor-chrome");
    if (!chrome) {
      chrome = document.createElement("div");
      chrome.id = "dungeon-floor-chrome";
      chrome.className = "dungeon-floor-chrome";
      root.appendChild(chrome);
    }
    chrome.innerHTML = `
      <header class="dungeon-floor-chrome-head">
        <div class="dungeon-floor-chrome-head-main">
          <button type="button" class="btn btn-ghost btn-sm" id="dungeon-floor-back">← ด่าน</button>
          <h1 class="dungeon-floor-chrome-title">ด่าน ${editingFloor}</h1>
        </div>
        <div class="dungeon-floor-chrome-head-actions">
          <label class="dungeon-floor-enable-toggle gm-tower-enabled-field">
            <input type="checkbox" id="dungeon-enabled" ${draftEnabled ? "checked" : ""} />
            <span>เปิดด่านนี้</span>
          </label>
          <p class="hint dungeon-floor-chrome-status" id="dungeon-floor-status" role="status">${escapeHtml(status)}</p>
          <button type="button" class="btn btn-ghost btn-sm" id="dungeon-floor-trial">ทดลอง</button>
          <button type="button" class="btn btn-primary btn-sm" id="dungeon-floor-save">บันทึก</button>
        </div>
      </header>
      <aside class="dungeon-floor-chrome-aside dungeon-floor-aside" id="dungeon-aside" aria-label="ตั้งค่าด่าน">${renderAside()}</aside>
    `;
  };

  const refreshAside = () => {
    const aside = root.querySelector<HTMLElement>("#dungeon-aside");
    if (aside) aside.innerHTML = renderAside();
    bindAsideEvents();
    syncReadinessPanel();
    syncEnableCheckbox();
  };

  const setStatus = (message: string) => {
    status = message;
    const el = root.querySelector<HTMLElement>("#dungeon-floor-status");
    if (el) el.textContent = message;
  };

  const draftSpawnsForTrial = () =>
    draftRows
      .filter((r) => r.monsterId)
      .map((r) => ({
        monsterId: r.monsterId,
        count: r.count,
        powerMultiplier: r.powerMultiplier ?? 1,
        spawnPerMinute: clampSpawnPerMinute(r.spawnPerMinute ?? TOWER_DEFAULT_SPAWN_PER_MINUTE),
      }));

  const adjustRowCount = (rowId: string, delta: number) => {
    const row = draftRows.find((r) => r.rowId === rowId);
    if (!row) return;
    const cap = maxForMonster(row.monsterId);
    const next = row.count + delta;
    if (next > cap) {
      setStatus(`จำนวนสูงสุด ${cap.toLocaleString("en-US")} (คลังไม่พอ)`);
      return;
    }
    row.count = Math.max(1, next);
    refreshAside();
  };

  const adjustRowPower = (rowId: string, delta: number) => {
    const row = draftRows.find((r) => r.rowId === rowId);
    if (!row) return;
    const current = row.powerMultiplier ?? 1;
    if (delta < 0) {
      row.powerMultiplier = stepDungeonMonsterPowerDown(current);
      refreshAside();
      return;
    }
    const res = applyDungeonMonsterPowerCard(economy, shop, current);
    if (!res.ok) {
      setStatus(res.reason);
      return;
    }
    economy = res.economy;
    persistEconomy();
    row.powerMultiplier = res.powerMultiplier;
    refreshAside();
  };

  const bindAsideEvents = () => {
    root.querySelectorAll<HTMLButtonElement>(".dungeon-spawn-list-open").forEach((btn) => {
      btn.addEventListener("click", () => {
        const id = btn.dataset.rowId;
        const row = draftRows.find((r) => r.rowId === id);
        if (row) spawnModal.openForRow(row);
      });
    });

    root.querySelectorAll<HTMLButtonElement>("[data-row-count-minus]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const id = (btn as HTMLElement).dataset.rowId;
        if (id) adjustRowCount(id, -1);
      });
    });
    root.querySelectorAll<HTMLButtonElement>("[data-row-count-plus]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const id = (btn as HTMLElement).dataset.rowId;
        if (id) adjustRowCount(id, 1);
      });
    });
    root.querySelectorAll<HTMLButtonElement>("[data-row-power-minus]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const id = (btn as HTMLElement).dataset.rowId;
        if (id) adjustRowPower(id, -1);
      });
    });
    root.querySelectorAll<HTMLButtonElement>("[data-row-power-plus]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const id = (btn as HTMLElement).dataset.rowId;
        if (id) adjustRowPower(id, 1);
      });
    });

    root.querySelectorAll<HTMLButtonElement>(".dungeon-spawn-list-remove").forEach((btn) => {
      btn.addEventListener("click", () => {
        const id = btn.dataset.rowId;
        draftRows = draftRows.filter((r) => r.rowId !== id);
        refreshAside();
      });
    });

    root.querySelector("#dungeon-add-row")?.addEventListener("click", () => {
      if (draftRows.length >= DUNGEON_FLOOR_MAX_MONSTER_TYPES) {
        setStatus(`ใส่ได้ไม่เกิน ${DUNGEON_FLOOR_MAX_MONSTER_TYPES} ประเภท`);
        return;
      }
      monsterPicker.open();
    });
  };

  const bindChromeEvents = () => {
    root.querySelector<HTMLInputElement>("#dungeon-enabled")?.addEventListener("change", (event) => {
      const input = event.currentTarget as HTMLInputElement;
      if (!input.checked) {
        draftEnabled = false;
        syncReadinessPanel();
        return;
      }
      const missing = collectMissingForEnable();
      if (missing.length > 0) {
        input.checked = false;
        draftEnabled = false;
        setStatus(dungeonFloorEnableBlockedMessage(missing));
        syncReadinessPanel();
        return;
      }
      draftEnabled = true;
      setStatus("พร้อมเปิดด่าน — กดบันทึกเพื่อเก็บ");
      syncReadinessPanel();
    });
  };

  const gameArt = profileToGameArt(profile);
  editor = await mountHomeSceneEditor(root, {
    user,
    profile,
    profileName: profile.name,
    avatarSrc: gameArt.playerIdleTextureUrl || HOME_ASSETS.avatarDefault,
    gameArt,
    sceneId: dungeonSceneId(editingFloor),
    hudSubtitle: `ด่าน ${editingFloor}`,
    pageRootClass: "game-app page-my-home page-dungeon-floor",
    shell: "home",
    features: DUNGEON_FLOOR_EDITOR_FEATURES,
    arenaLayout: true,
    fixedGrid: { cols: TOWER_ARENA_COLS, rows: TOWER_ARENA_ROWS },
    autoEnterEditMode: true,
    loadRoom: async () => {
      const saved = await loadSceneRoom(user.uid, dungeonSceneId(editingFloor));
      const home = await loadHomeRoom(user.uid);
      return applyDungeonFloorLayoutFromConfig(
        mergeHomeCatalogParts(saved, home),
        sceneRoom,
      );
    },
    saveRoom: async (data) => {
      sceneRoom = data;
      await saveSceneRoom(user.uid, dungeonSceneId(editingFloor), data);
    },
  });

  root.querySelector("#dungeon-floor-trial")?.addEventListener("click", () => {
    void (async () => {
      editor?.flushSave();
      const room = editor?.getRoomData() ?? sceneRoom;
      sceneRoom = room;
      const spawns = draftSpawnsForTrial();
      if (spawns.length === 0) {
        setStatus("เพิ่มมอนสเตอร์ก่อนทดลอง");
        return;
      }
      void openDungeonTrialModal(user, {
        onlyFloor: editingFloor,
        floorTrialPreview: {
          floor: editingFloor,
          enabled: true,
          spawns,
          scenePlacements: resolveDungeonFloorScenePlacements(room, cfg?.scenePlacements),
          sceneAmbience: buildTowerArenaAmbienceConfig(room),
        },
      });
    })();
  });

  const spawnModal = mountDungeonFloorMonsterModal(root, {
    catalog,
    shop,
    getEconomy: () => economy,
    maxForMonster,
    vaultCount: (id) => vaultStock(economy, id),
    selectableMonsterIds,
    onEconomyUpdate: (next) => {
      economy = next;
      persistEconomy();
    },
    onApply: (row) => {
      const idx = draftRows.findIndex((r) => r.rowId === row.rowId);
      if (idx >= 0) {
        draftRows[idx] = row;
      } else {
        draftRows.push(row);
      }
      refreshAside();
    },
    onStatus: setStatus,
  });

  const monsterPicker = mountDungeonFloorMonsterPickerModal(root, {
    catalog,
    listMonsterIds: () => selectableMonsterIds(),
    vaultCount: (id) => vaultStock(economy, id),
    maxForMonster,
    onPick: (monsterId) => {
      if (draftRows.length >= DUNGEON_FLOOR_MAX_MONSTER_TYPES) {
        setStatus(`ใส่ได้ไม่เกิน ${DUNGEON_FLOOR_MAX_MONSTER_TYPES} ประเภท`);
        return;
      }
      if (maxForMonster(monsterId) <= 0) {
        setStatus("คลังมอนสเตอร์ไม่พอ — ซื้อที่ร้าน");
        return;
      }
      const rowId = nextRowId();
      const row: DungeonSpawnDraftRow = {
        rowId,
        monsterId,
        count: 1,
        powerMultiplier: 1,
        spawnPerMinute: TOWER_DEFAULT_SPAWN_PER_MINUTE,
      };
      draftRows.push(row);
      refreshAside();
      spawnModal.openForRow(row);
    },
  });

  mountDungeonChrome();
  bindChromeEvents();
  bindAsideEvents();

  root.querySelector("#dungeon-floor-back")?.addEventListener("click", () => {
    options?.onRequestClose?.();
  });

  if (draftEnabled && !dungeonFloorCanEnable({
    spawns: draftRows,
    economy,
    savedSpawnsOnFloor,
    scenePlacementCount: sceneRoom.placements.length,
    monsterName: monsterLabel,
  })) {
    setStatus(dungeonFloorEnableBlockedMessage(collectMissingForEnable()));
  }

  root.querySelector("#dungeon-floor-save")?.addEventListener("click", () => {
    void (async () => {
      editor?.flushSave();
      const room = editor?.getRoomData() ?? sceneRoom;
      sceneRoom = room;
      draftEnabled = root.querySelector<HTMLInputElement>("#dungeon-enabled")?.checked ?? false;

      const missing = collectMissingForEnable();
      if (draftEnabled && missing.length > 0) {
        draftEnabled = false;
        const enabledInput = root.querySelector<HTMLInputElement>("#dungeon-enabled");
        if (enabledInput) enabledInput.checked = false;
        setStatus(dungeonFloorEnableBlockedMessage(missing));
        syncReadinessPanel();
        return;
      }

      const spawns = draftRows
        .filter((r) => r.monsterId)
        .map((r) => ({
          monsterId: r.monsterId,
          count: r.count,
          powerMultiplier: r.powerMultiplier ?? 1,
          spawnPerMinute: clampSpawnPerMinute(r.spawnPerMinute ?? TOWER_DEFAULT_SPAWN_PER_MINUTE),
        }));

      const vaultCommit = commitFloorSpawnsToVault(economy, savedSpawnsOnFloor, spawns);
      if (!vaultCommit.ok) {
        setStatus(vaultCommit.reason);
        return;
      }
      economy = vaultCommit.economy;
      persistEconomy();

      await saveDungeonFloorConfig(user.uid, {
        floor: editingFloor,
        spawns,
        enabled: draftEnabled,
        powerMultiplier: 1,
        scenePlacements: homeRoomToTowerScenePlacements(room),
        updatedAt: Date.now(),
      });
      savedSpawnsOnFloor = spawns;
      setStatus(draftEnabled ? "บันทึกแล้ว · ด่านเปิดใช้งาน" : "บันทึกแล้ว · ด่านปิดอยู่");
      syncReadinessPanel();
    })();
  });

  syncReadinessPanel();

  const onRoomDataChanged = (event: Event) => {
    const detail = (event as CustomEvent<{ data?: HomeRoomData }>).detail;
    if (!detail?.data) return;
    sceneRoom = detail.data;
    syncReadinessPanel();
  };
  window.addEventListener(HomeEvents.DataChanged, onRoomDataChanged);

  return () => {
    window.removeEventListener(HomeEvents.DataChanged, onRoomDataChanged);
    monsterPicker.destroy();
    spawnModal.destroy();
    editor?.destroy();
    root.querySelector("#dungeon-floor-chrome")?.remove();
  };
}
